import { useState, useEffect, useCallback } from 'react'
import { X, Send, Mail, ChevronDown } from 'lucide-react'

interface EmailComposeModalProps {
  isOpen: boolean
  onClose: () => void
  recipientEmail: string
  recipientName: string
  onSend: (data: { subject: string; body: string }) => void
  isPending?: boolean
  error?: string | null
}

export function EmailComposeModal({
  isOpen,
  onClose,
  recipientEmail,
  recipientName,
  onSend,
  isPending = false,
  error,
}: EmailComposeModalProps) {
  if (!isOpen) return null

  return (
    <EmailComposeModalContent
      onClose={onClose}
      recipientEmail={recipientEmail}
      recipientName={recipientName}
      onSend={onSend}
      isPending={isPending}
      error={error}
    />
  )
}

interface EmailComposeModalContentProps {
  onClose: () => void
  recipientEmail: string
  recipientName: string
  onSend: (data: { subject: string; body: string }) => void
  isPending: boolean
  error?: string | null
}

const EMAIL_TEMPLATES: { id: string; name: string; subject: string; body: string }[] = [
  {
    id: 'invitation',
    name: 'Invitation',
    subject: "You're invited to be a guest on our podcast",
    body: `Hi {{name}},

I'd love to have you as a guest on our podcast to discuss your expertise.

Would you be interested in joining us for a conversation?

Looking forward to hearing from you!

Best regards`,
  },
  {
    id: 'follow_up',
    name: 'Follow Up',
    subject: 'Following up on our podcast invitation',
    body: `Hi {{name}},

I wanted to follow up on my previous message about joining us as a podcast guest.

We think your insights would be valuable for our audience and would love to have you on the show.

Let me know if you're interested or if you have any questions!

Best regards`,
  },
  {
    id: 'thank_you',
    name: 'Thank You',
    subject: 'Thank you for being on our podcast!',
    body: `Hi {{name}},

Thank you so much for taking the time to be a guest on our podcast! It was a fantastic conversation.

We'll let you know when the episode goes live. In the meantime, feel free to reach out if you have any questions.

Thanks again for your time and insights!

Best regards`,
  },
  {
    id: 'custom',
    name: 'Custom Email',
    subject: '',
    body: '',
  },
]

function EmailComposeModalContent({
  onClose,
  recipientEmail,
  recipientName,
  onSend,
  isPending,
  error,
}: EmailComposeModalContentProps) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<string>('custom')
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false)

  // Handle Escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowTemplateDropdown(false)
    if (showTemplateDropdown) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showTemplateDropdown])

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId)
    setShowTemplateDropdown(false)

    const template = EMAIL_TEMPLATES.find((t) => t.id === templateId)
    if (template) {
      // Replace {{name}} placeholder with actual name
      const processedSubject = template.subject.replace(/\{\{name\}\}/g, recipientName)
      const processedBody = template.body.replace(/\{\{name\}\}/g, recipientName)
      setSubject(processedSubject)
      setBody(processedBody)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!subject.trim() || !body.trim()) return
    onSend({ subject: subject.trim(), body: body.trim() })
  }

  const selectedTemplateName =
    EMAIL_TEMPLATES.find((t) => t.id === selectedTemplate)?.name || 'Custom Email'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-compose-modal-title"
      data-testid="email-compose-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
        data-testid="email-compose-modal-backdrop"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
          <h2
            id="email-compose-modal-title"
            className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2"
          >
            <Mail className="w-5 h-5 text-[#FF6F00]" />
            Compose Email
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Close modal"
            data-testid="email-compose-modal-close"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
            {/* Error message */}
            {error && (
              <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800" data-testid="email-compose-modal-error">
                <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
              </div>
            )}

            {/* To field */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                To
              </label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/50">
                <span className="text-sm text-zinc-900 dark:text-zinc-100">{recipientName}</span>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">&lt;{recipientEmail}&gt;</span>
              </div>
            </div>

            {/* Template selector */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Template
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowTemplateDropdown(!showTemplateDropdown)
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition-colors text-left"
                  data-testid="email-compose-template-dropdown"
                >
                  <span className="text-sm">{selectedTemplateName}</span>
                  <ChevronDown className="w-4 h-4 text-zinc-400" />
                </button>

                {showTemplateDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg py-1" data-testid="email-compose-template-list">
                    {EMAIL_TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTemplateSelect(template.id)
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors ${
                          selectedTemplate === template.id
                            ? 'bg-zinc-100 dark:bg-zinc-700 text-[#FF6F00]'
                            : 'text-zinc-900 dark:text-zinc-100'
                        }`}
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Subject field */}
            <div>
              <label
                htmlFor="email-subject"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Subject
              </label>
              <input
                id="email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                disabled={isPending}
                maxLength={200}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent disabled:opacity-50"
                autoFocus
                data-testid="email-compose-subject"
              />
            </div>

            {/* Body field */}
            <div>
              <label
                htmlFor="email-body"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Message
              </label>
              <textarea
                id="email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your email message..."
                rows={10}
                disabled={isPending}
                maxLength={10000}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent disabled:opacity-50 resize-none"
                data-testid="email-compose-body"
              />
              <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400" data-testid="email-compose-char-count">
                {body.length.toLocaleString()} / 10,000 characters
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 flex-shrink-0">
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
              Replies will be sent to your email inbox, not Floaty.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
                data-testid="email-compose-cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!subject.trim() || !body.trim() || isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#FF6F00] hover:bg-[#FF6F00]/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="email-compose-send-button"
              >
                <Send className="w-4 h-4" />
                {isPending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
